<template>
  <Teleport to="body">
    <Transition name="celebration-fade">
      <div
        v-if="isOpen"
        class="game-won-overlay"
        :class="{ 'has-video': useVideoMode }"
        role="dialog"
        aria-modal="true"
        aria-label="Game Won Celebration"
      >
        <!-- AI-Generated Video Background (Nano Banana) -->
        <video
          v-if="useVideoMode && celebrationVideo"
          ref="videoRef"
          class="celebration-video"
          :src="videoSource"
          autoplay
          muted
          playsinline
          @ended="onVideoEnded"
          @error="onVideoError"
        />

        <!-- Fire glow backdrop (fallback mode only) -->
        <div v-if="!useVideoMode" class="fire-glow"></div>

        <!-- Smoke canvas container (fallback mode only) -->
        <div v-if="!useVideoMode" class="smoke-container" ref="smokeContainerRef"></div>

        <!-- 3D Trophy container (fallback mode only) -->
        <div
          v-if="!useVideoMode"
          class="trophy-container"
          :class="{ risen: trophyRisen }"
          ref="trophyContainerRef"
        ></div>

        <!-- Sparks canvas container (both modes - adds flair to video) -->
        <div class="spark-container" ref="sparkContainerRef"></div>

        <!-- Victory text with dynamic positioning -->
        <Transition name="text-slam">
          <div
            v-if="showText"
            class="victory-text"
            :class="{ 'video-positioned': useVideoMode }"
            :style="textPositionStyle"
          >
            <h1 class="team-name" :class="`team-${winningTeam.toLowerCase()}`">
              {{ winningTeamName }} Wins!
            </h1>
            <div class="final-score" :style="scorePositionStyle">
              {{ winningScore.toLocaleString() }} points
            </div>
          </div>
        </Transition>

        <!-- Victory quip -->
        <Transition name="fade-up">
          <p v-if="showQuip" class="victory-quip">
            "{{ currentQuip }}"
          </p>
        </Transition>

        <!-- Action buttons -->
        <Transition name="buttons-slide">
          <div v-if="showButtons" class="action-buttons">
            <button @click="handleRematch" class="btn-rematch">
              <span class="material-symbols-outlined">restart_alt</span>
              <span>Rematch</span>
            </button>
            <button @click="handleLobby" class="btn-lobby">
              <span class="material-symbols-outlined">home</span>
              <span>Return to Lobby</span>
            </button>
          </div>
        </Transition>

        <!-- Skip button (accessibility) -->
        <button
          v-if="!showButtons"
          @click="skipToEnd"
          class="skip-button"
          aria-label="Skip celebration animation"
        >
          Skip
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useTrophyScene } from '@/composables/useTrophyScene'
import { useSmokeCanvas } from '@/composables/useSmokeCanvas'
import { useSparkCanvas } from '@/composables/useSparkCanvas'
import { useVictoryVideo, type CelebrationVideo } from '@/composables/useVictoryVideo'

/**
 * GameWonCelebration Component
 *
 * Premium celebration overlay with AI-generated video backgrounds.
 * Uses Nano Banana + Veo generated videos when available, with
 * fallback to Three.js trophy celebration.
 *
 * ★ Insight ─────────────────────────────────────
 * This component orchestrates multiple visual systems:
 * - AI-generated celebration videos (Nano Banana + Veo)
 * - Three.js 3D trophy (fallback - procedural geometry with PBR)
 * - Canvas-based smoke/spark particles
 * - CSS animations for text and buttons
 *
 * Video mode: Background video + spark overlay + dynamic text
 * Fallback mode: Trophy + smoke + sparks + centered text
 * ─────────────────────────────────────────────────
 */

// Props
interface Props {
  isOpen: boolean
  winningTeam: 'A' | 'B'
  teamAName: string
  teamBName: string
  teamAScore: number
  teamBScore: number
  forceVideoMode?: boolean  // For testing: force video even if not loaded
  forceFallback?: boolean   // For testing: force fallback mode
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  winningTeam: 'A',
  teamAName: 'Team A',
  teamBName: 'Team B',
  teamAScore: 0,
  teamBScore: 0,
  forceVideoMode: false,
  forceFallback: false,
})

// Emits
const emit = defineEmits<{
  rematch: []
  'return-to-lobby': []
  complete: []
}>()

// Victory quips - style-specific variants
const VICTORY_QUIPS = {
  default: [
    "That's what I call a winning hand!",
    "Canasta champions in the house!",
    "10,000 points of pure glory!",
    "The cards have spoken!",
    "Victory tastes like... canastas!",
    "Bow before the card masters!",
    "That was absolutely legendary!",
    "A game for the ages!",
    "May the cards forever be in your favor!",
    "And the crowd goes wild!",
    "This calls for a victory dance!",
  ],
  heist: [
    "The house always wins... wait, that's us!",
    "All according to plan.",
    "The perfect score.",
    "You just got dealt out.",
    "We came, we saw, we carded.",
  ],
  gallery: [
    "A masterpiece of card play!",
    "Gallery-worthy performance!",
    "This belongs in a museum!",
    "Pure artistry on the table.",
    "Critics agree: flawless!",
  ],
  royalty: [
    "The royal court has spoken!",
    "Long live the card monarchs!",
    "By royal decree: VICTORY!",
    "The crown belongs to the worthy.",
    "All hail the canasta kings!",
  ],
}

// Animation state
const trophyRisen = ref(false)
const showText = ref(false)
const showQuip = ref(false)
const showButtons = ref(false)
const currentQuip = ref('')
const videoError = ref(false)

// Container refs
const smokeContainerRef = ref<HTMLElement | null>(null)
const trophyContainerRef = ref<HTMLElement | null>(null)
const sparkContainerRef = ref<HTMLElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)

// Video composable
const victoryVideo = useVictoryVideo()
const celebrationVideo = ref<CelebrationVideo | null>(null)

// Composables for 3D trophy, smoke, and sparks (fallback mode)
const trophyScene = useTrophyScene(trophyContainerRef)
const smokeCanvas = useSmokeCanvas(smokeContainerRef)
const sparkCanvas = useSparkCanvas(sparkContainerRef)

// Check for reduced motion preference
const prefersReducedMotion = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false

// Computed: Determine if we should use video mode
const useVideoMode = computed(() => {
  if (props.forceFallback) return false
  if (props.forceVideoMode) return true
  return victoryVideo.hasVideosAvailable.value && !videoError.value
})

// Computed: Video source URL
const videoSource = computed(() => {
  if (!celebrationVideo.value) return ''
  return victoryVideo.getVideoSource(celebrationVideo.value)
})

// Computed: Text position based on video or default
const textPositionStyle = computed(() => {
  if (!useVideoMode.value || !celebrationVideo.value) {
    return {} // Use default CSS positioning
  }
  const pos = celebrationVideo.value.textPosition
  return {
    position: 'absolute' as const,
    left: pos.x,
    top: pos.y,
    transform: pos.align === 'center' ? 'translateX(-50%)' : undefined,
    textAlign: pos.align || 'center',
  }
})

// Computed: Score position offset from text
const scorePositionStyle = computed(() => {
  if (!useVideoMode.value || !celebrationVideo.value) {
    return {}
  }
  // Score follows text, just adds margin
  return {}
})

// Computed
const winningTeamName = computed(() =>
  props.winningTeam === 'A' ? props.teamAName : props.teamBName
)

const winningScore = computed(() =>
  props.winningTeam === 'A' ? props.teamAScore : props.teamBScore
)

// Utility sleep function
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Play sound effect (if SoundManager exists)
function playSound(sound: string): void {
  try {
    const soundManager = (window as any).SoundManager
    if (soundManager?.playVictoryFanfare && sound === 'victory') {
      soundManager.playVictoryFanfare()
    } else if (soundManager?.play) {
      soundManager.play(sound)
    }
  } catch {
    // Sound not available, that's okay
  }
}

/**
 * Select a quip based on video style or default
 */
function selectQuip(style?: string): string {
  const quipList = style && style in VICTORY_QUIPS
    ? VICTORY_QUIPS[style as keyof typeof VICTORY_QUIPS]
    : VICTORY_QUIPS.default
  const selectedQuip = quipList[Math.floor(Math.random() * quipList.length)]
  return selectedQuip ?? VICTORY_QUIPS.default[0] ?? "Victory!"
}

/**
 * Video ended handler - show buttons
 */
function onVideoEnded(): void {
  // Video complete, ensure all UI is shown
  showText.value = true
  showQuip.value = true
  showButtons.value = true
  sparkCanvas.setSettled()
  emit('complete')
}

/**
 * Video error handler - fallback to trophy mode
 */
function onVideoError(): void {
  console.warn('[GameWonCelebration] Video failed to load, using fallback')
  videoError.value = true
  // Re-run celebration in fallback mode
  runFallbackCelebration()
}

/**
 * Main animation sequence - determines mode and runs appropriate flow
 */
async function runCelebration(): Promise<void> {
  // Select video if available
  if (victoryVideo.hasVideosAvailable.value && !props.forceFallback) {
    celebrationVideo.value = victoryVideo.selectRandom()
  }

  // Select quip based on video style
  const style = celebrationVideo.value?.style
  currentQuip.value = selectQuip(style)

  // Reduced motion: skip to end state
  if (prefersReducedMotion) {
    trophyRisen.value = true
    showText.value = true
    showQuip.value = true
    showButtons.value = true
    emit('complete')
    return
  }

  // Determine which celebration to run
  if (useVideoMode.value && celebrationVideo.value) {
    await runVideoCelebration()
  } else {
    await runFallbackCelebration()
  }
}

/**
 * Video-based celebration sequence
 */
async function runVideoCelebration(): Promise<void> {
  // Start sparks (adds flair on top of video)
  sparkCanvas.start()

  // Play victory fanfare
  playSound('victory')

  // Video autoplays - timing based on video duration
  const duration = celebrationVideo.value?.duration ?? 7

  // Show text at 40% of video
  await sleep(duration * 0.4 * 1000)
  showText.value = true

  // Show quip at 55% of video
  await sleep(duration * 0.15 * 1000)
  showQuip.value = true

  // Reduce spark rate as video winds down
  sparkCanvas.setSettled()

  // Show buttons at 80% of video (or wait for video end)
  await sleep(duration * 0.25 * 1000)
  showButtons.value = true

  emit('complete')
}

/**
 * Fallback celebration using Three.js trophy (original behavior)
 */
async function runFallbackCelebration(): Promise<void> {
  // Start smoke effect immediately
  smokeCanvas.start()

  // Start sparks effect
  sparkCanvas.start()

  // Play victory fanfare
  playSound('victory')

  // Start 3D trophy scene
  await trophyScene.start()
  trophyRisen.value = true

  // Trophy has settled - reduce spark rate
  sparkCanvas.setSettled()

  // 3.0s - Show team name (trophy has risen by now)
  await sleep(2500)
  showText.value = true

  // 4.0s - Show quip
  await sleep(1000)
  showQuip.value = true

  // 5.5s - Show buttons
  await sleep(1500)
  showButtons.value = true

  emit('complete')
}

// Skip to end
function skipToEnd(): void {
  trophyRisen.value = true
  showText.value = true
  showQuip.value = true
  showButtons.value = true

  // Skip video to end if playing
  if (videoRef.value && useVideoMode.value) {
    videoRef.value.currentTime = videoRef.value.duration || 0
  }

  // Start systems if not already running (fallback mode)
  if (!useVideoMode.value) {
    if (!smokeCanvas.isRunning.value) {
      smokeCanvas.start()
    }
    if (!trophyScene.isRunning.value) {
      trophyScene.start()
    }
  }

  if (!sparkCanvas.isRunning.value) {
    sparkCanvas.start()
    sparkCanvas.setSettled()
  }

  emit('complete')
}

// Reset state
function resetState(): void {
  trophyRisen.value = false
  showText.value = false
  showQuip.value = false
  showButtons.value = false
  currentQuip.value = ''
  videoError.value = false
  celebrationVideo.value = null

  // Reset video element
  if (videoRef.value) {
    videoRef.value.currentTime = 0
    videoRef.value.pause()
  }

  // Reset composables
  smokeCanvas.stop()
  sparkCanvas.stop()
  trophyScene.stop()
  trophyScene.reset()
}

// Handle rematch
function handleRematch(): void {
  emit('rematch')
}

// Handle return to lobby
function handleLobby(): void {
  emit('return-to-lobby')
}

// Watch for open state
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    resetState()
    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      runCelebration()
    })
  } else {
    resetState()
  }
})

// Cleanup on unmount
onUnmounted(() => {
  smokeCanvas.destroy()
  sparkCanvas.destroy()
  trophyScene.destroy()
  victoryVideo.cleanup()
})
</script>

<style scoped>
/* Main overlay - warm festive theme */
.game-won-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(25, 25, 50, 0.98) 0%,
    rgba(40, 20, 60, 0.98) 30%,
    rgba(60, 30, 40, 0.95) 60%,
    rgba(80, 40, 20, 0.95) 100%
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10500;
  overflow: hidden;
}

/* Video mode overlay - darker background for video contrast */
.game-won-overlay.has-video {
  background: rgba(0, 0, 0, 0.9);
}

/* AI-Generated Celebration Video (Nano Banana + Veo) */
.celebration-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  /* Slight cinematic overlay for text readability */
  filter: brightness(0.9) contrast(1.1);
}

/* Fire glow backdrop */
.fire-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 80% 60% at center 80%,
    rgba(255, 120, 50, 0.5) 0%,
    rgba(255, 80, 30, 0.3) 30%,
    rgba(200, 50, 20, 0.15) 50%,
    transparent 70%
  );
  animation: glow-pulse 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Smoke container (full overlay, behind trophy) */
.smoke-container {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

/* Spark container (full overlay, above trophy) */
.spark-container {
  position: absolute;
  inset: 0;
  z-index: 15;
  pointer-events: none;
}

/* 3D Trophy container */
.trophy-container {
  position: relative;
  width: 400px;
  height: 400px;
  z-index: 10;
  /* Remove the CSS transform animation - let Three.js handle it */
  /* transform: translateY(100vh); */
  /* transition: transform 2.5s cubic-bezier(0.34, 1.2, 0.64, 1); */
}

.trophy-container.risen {
  /* transform: translateY(0); */
}

/* Victory text */
.victory-text {
  text-align: center;
  margin-top: 2rem;
  z-index: 20;
}

/* Video mode: absolute positioning with enhanced text shadow */
.victory-text.video-positioned {
  margin: 0;
  padding: 1rem 2rem;
  /* Glass-morphism background for readability over video */
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.team-name {
  font-size: 4rem;
  font-weight: 800;
  margin: 0;
  text-shadow:
    0 0 20px rgba(255, 215, 0, 0.8),
    0 0 40px rgba(255, 165, 0, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.4);
  background: linear-gradient(
    135deg,
    #ffd700 0%,
    #ffec8b 25%,
    #fffacd 50%,
    #ffec8b 75%,
    #ffd700 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gold-shimmer 3s linear infinite;
}

.team-name.team-a {
  filter: hue-rotate(-10deg);
}

.team-name.team-b {
  filter: hue-rotate(10deg);
}

@keyframes gold-shimmer {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.final-score {
  font-size: 1.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 0.5rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

/* Victory quip */
.victory-quip {
  font-size: 1.5rem;
  font-style: italic;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 1.5rem;
  text-align: center;
  max-width: 500px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  z-index: 20;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 1.5rem;
  margin-top: 3rem;
  z-index: 20;
}

.btn-rematch,
.btn-lobby {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-rematch {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.btn-rematch:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.5);
}

.btn-lobby {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
}

.btn-lobby:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-3px);
}

.btn-rematch:active,
.btn-lobby:active {
  transform: translateY(0);
}

.btn-rematch .material-symbols-outlined,
.btn-lobby .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Skip button */
.skip-button {
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 30;
}

.skip-button:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.2);
}

/* Transitions */
.celebration-fade-enter-active {
  transition: opacity 0.5s ease;
}

.celebration-fade-leave-active {
  transition: opacity 0.3s ease;
}

.celebration-fade-enter-from,
.celebration-fade-leave-to {
  opacity: 0;
}

.text-slam-enter-active {
  animation: text-slam 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slam {
  0% {
    transform: scale(3) translateY(-30px);
    opacity: 0;
    filter: blur(10px);
  }
  60% {
    transform: scale(0.95);
    filter: blur(0);
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.fade-up-enter-active {
  transition: all 0.5s ease;
}

.fade-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.buttons-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.buttons-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

/* Responsive */
@media (max-width: 768px) {
  .trophy-container {
    width: 300px;
    height: 300px;
  }

  .team-name {
    font-size: 2.5rem;
  }

  .final-score {
    font-size: 1.25rem;
  }

  .victory-quip {
    font-size: 1.125rem;
    padding: 0 1rem;
  }

  .action-buttons {
    flex-direction: column;
    gap: 1rem;
    width: 90%;
    max-width: 300px;
  }

  .btn-rematch,
  .btn-lobby {
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .trophy-container {
    width: 250px;
    height: 250px;
  }

  .team-name {
    font-size: 2rem;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .trophy-container {
    transition: none;
    transform: translateY(0);
  }

  .fire-glow {
    animation: none;
  }

  .team-name {
    animation: none;
  }
}
</style>
